import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { AccessModel, AppContentType } from '@jwp/ott-common/types/config';
import { isLocked } from '@jwp/ott-common/src/utils/entitlements';
import { mediaURL, seeMoreUrl } from '@jwp/ott-common/src/utils/urlFormatting';
import { PersonalShelf } from '@jwp/ott-common/src/constants';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import useBreakpoint, { Breakpoint, type Breakpoints } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import type { PosterAspectRatio } from '@jwp/ott-common/src/utils/collection';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';

import Card from '../Card/Card';
import { TileSlider } from '../TileDock/TileDock';
import Icon from '../Icon/Icon';
import createInjectableComponent from '../../modules/createInjectableComponent';
import { useTheme } from '../../../../../ThemeContext';

import styles from './Shelf.module.scss';
import { useNavigationStack } from '@jwp/ott-common/src/stores/NavigationStackStore';

import { useInView } from 'react-intersection-observer';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

export type Direction = 'left' | 'right';
export type CycleMode = 'stop' | 'restart' | 'endless';
export type RenderTile<T> = (params: {
  item: T;
  itemIndex: number;
  isVisible: boolean;
  index: number;
  slide: (direction: Direction) => void;
}) => React.ReactElement;

export type RenderControl = (props: ControlProps) => React.ReactElement;
export type RenderPagination = (props: PaginationProps) => React.ReactElement;
export type ControlProps = {
  onClick: () => void;
  disabled: boolean;
};
export type PaginationProps = {
  index: number;
  itemIndex: number;
  total: number;
  page: number;
  pages: number;
  slide: (direction: Direction) => void;
  slideToIndex: (index: number) => void;
  slideToPage: (page: number) => void;
};
export const tileBreakpoints: Breakpoints = {
  [Breakpoint.xs]: 1,
  [Breakpoint.sm]: 2,
  [Breakpoint.md]: 3,
  [Breakpoint.lg]: 4,
  [Breakpoint.xl]: 5,
};

export const featuredTileBreakpoints: Breakpoints = {
  [Breakpoint.xs]: 1,
  [Breakpoint.sm]: 1,
  [Breakpoint.md]: 1,
  [Breakpoint.lg]: 1,
  [Breakpoint.xl]: 1,
};

export const ShelfIdentifier = Symbol(`SHELF`);

export type ShelfProps = {
  index: number;
  playlist: Playlist;
  type: AppContentType;
  onCardHover?: (playlistItem: PlaylistItem) => void;
  watchHistory?: { [key: string]: number };
  enableTitle?: boolean;
  enableCardTitles?: boolean;
  featured?: boolean;
  loading?: boolean;
  error?: unknown;
  title?: string;
  accessModel: AccessModel;
  isLoggedIn: boolean;
  hasSubscription: boolean;
  posterAspect?: PosterAspectRatio;
  visibleTilesDelta?: number;
  customShelfValues?: any;
  liveEventPlayListCall: () => void;
  onLastItemClick?: () => void;
  clickLabel: string;
  activeScreen: string;
  pageId?: string;
  onShelfSeen?: (index: number) => void;
};

const Shelf = ({
  index,
  playlist,
  type,
  onCardHover,
  title,
  watchHistory,
  featured = false,
  loading = false,
  error = null,
  accessModel,
  isLoggedIn,
  hasSubscription,
  posterAspect,
  visibleTilesDelta = 0,
  customShelfValues,
  liveEventPlayListCall,
  onLastItemClick,
  onShelfSeen,
  clickLabel,
  activeScreen,
  pageId,
}: ShelfProps) => {
  const breakpoint: Breakpoint = useBreakpoint();
  const { universalStyles, themeConfig } = useTheme();
  const { t } = useTranslation('common');
  const tilesToShow: number = (featured ? featuredTileBreakpoints[breakpoint] : tileBreakpoints[breakpoint]) + visibleTilesDelta;
  const navigate = useNavigate();
  const { pathname: parentPath } = useLocation();
  const { singleClickPlayEnabled } = useUIControls();
  const addToHistoryStack = useNavigationStack((s) => s.push);
  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);

  const rowState = useContentStateStore((s) => s.rows[`${pageId || parentPath}:${playlist.feedid}`]);
  const setRowState = useContentStateStore((s) => s.setRowState);

  const focusedIndex = rowState?.focusedIndex ?? 0;

  const seenScreens = useRef(new Set());
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();

  const isShowTopTenIcon = useCallback(() => {
    return playlist?.railType == 'top-rated';
  }, [playlist?.railType]);

  const isOverlayTestVisible = useCallback(() => {
    const customShelfValue = customShelfValues ?? {};

    const customShowTitle = Object.entries(customShelfValue).find(([key]) => key.replace(/_/g, '').toLowerCase() === 'showtitle')?.[1];
    if (typeof customShowTitle === 'string' && customShowTitle !== '') {
      return customShowTitle.toLowerCase() !== 'false';
    } else {
      const showTitleEntry = Object.entries(playlist).find(([key]) => key.replace(/_/g, '').toLowerCase() === 'showtitle');

      const showTitleValue = showTitleEntry?.[1];

      if (typeof showTitleValue === 'string' && showTitleValue !== '') {
        return showTitleValue.toLowerCase() !== 'false';
      } else {
        return true;
      }
    }
  }, [playlist]);

  const { ref, inView } = useInView({
    threshold: 0.3,
    rootMargin: '0px 0px -50% 0px',
  });

  useEffect(() => {
    if (!inView) return;
    sendGAEvent('screen_view', {
      firebase_screen_class: clickLabel,
      firebase_screen: activeScreen,
      device_type: deviceType,
      userid_no: userId,
    });

    const trySendEvent = () => {
      if (playlist?.title && !seenScreens.current.has(index)) {
        seenScreens.current.add(index);

        onShelfSeen?.(index);
      } else {
        setTimeout(trySendEvent, 10);
      }
    };

    trySendEvent();
  }, [inView, playlist, index]);

  const renderTile = useCallback(
    ({
      item,
      isVisible,
      itemIndex,
      index,
      isShowTopTenIcon,
      isOverlayTestVisible,
    }: {
      item: PlaylistItem;
      itemIndex: number;
      index: number;
      isVisible: boolean;
      topTenIndex?: number;
      isShowTopTenIcon: boolean;
      isOverlayTestVisible: boolean;
    }) => {
      const { mediaid: id, title } = item;
      const isSeriesContent = item.contentType == 'series';
      const url = mediaURL({
        id,
        title,
        playlistId: playlist.feedid,
        play: type === PersonalShelf.ContinueWatching || (!isSeriesContent && singleClickPlayEnabled),
        contentType: item.contentType,
        returnPath: parentPath,
      });
      const shelfTitle = playlist.title;

      return (
        <Card
          key={item.mediaid}
          progress={watchHistory ? watchHistory[item.mediaid] : undefined}
          onHover={typeof onCardHover === 'function' ? () => onCardHover(item) : undefined}
          featured={featured}
          disabled={!isVisible}
          loading={loading}
          isLocked={isLocked(accessModel, isLoggedIn, hasSubscription, item)}
          posterAspect={posterAspect}
          item={item}
          url={url}
          isShowTopTenIcon={isShowTopTenIcon}
          topTenIndex={itemIndex}
          railType={typeof playlist?.railType === 'string' ? playlist?.railType : ''}
          isOverlayTestVisible={isOverlayTestVisible}
          shelfTitle={shelfTitle}
          isReels={item?.contentType === 'reels'}
        />
      );
    },
    [watchHistory, onCardHover, featured, loading, accessModel, isLoggedIn, hasSubscription, posterAspect, playlist.feedid, type],
  );

  const renderRightControl: RenderControl = useCallback(
    ({ onClick, disabled }) => (
      <button style={{ display: disabled ? 'none' : 'block' }} className={styles.chevron} aria-label={t('slide_next')} onClick={onClick}>
        <Icon icon={ChevronRight} />
      </button>
    ),
    [t],
  );

  const renderLeftControl: RenderControl = useCallback(
    ({ onClick, disabled }) => (
      <button style={{ display: disabled ? 'none' : 'block' }} className={styles.chevron} aria-label={t('slide_previous')} onClick={onClick}>
        <Icon icon={ChevronLeft} />
      </button>
    ),
    [t],
  );

  const renderPagination: RenderPagination = ({ page, pages }) => {
    const items = Array.from({ length: pages }, (_, pageIndex) => pageIndex);

    return (
      <div className={styles.dotPagination}>
        <div aria-live="polite" className="hidden">
          {t('slide_indicator', { page: page + 1, pages })}
        </div>
        {featured && (
          <div aria-hidden="true" className={styles.dots}>
            {items.map((current) => (
              <span
                key={current}
                className={classNames(styles.dot, { [styles.active]: page === current })}
                style={page === current ? universalStyles.carouselPaginationActive : universalStyles.carouselPaginationInactive}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const onFocusChange = (index: any) => {
    if (playlist.feedid) {
      setRowState(pageId || parentPath, playlist.feedid, { focusedIndex: index });
    }
  };

  const haveSeemoreOption = ['Top 10 Movies', 'Top Ten Movies', 'Continue Watching', 'Top 10 Movies this Week', 'Top ten Movies this Week']
    .map((item) => item.toLowerCase())
    .includes(playlist.title.toLowerCase());

  const seeMoreRedirection = (playlist: any) => {
    const path = seeMoreUrl(playlist.feedid);

    addToHistoryStack(path);
    navigate(path, {
      state: {
        playlist,
        shelfTitle: title || playlist.title,
      },
    });
  };

  const getLiveEventPlaylists = useCallback((playlists: Playlist) => {
    const isLiveEvent = playlists?.contentType?.toLowerCase() === 'liveevent';
    if (isLiveEvent && Array.isArray(playlists.playlist) && playlists.playlist.length > 0) {
      const liveEvent: PlaylistItem[] = [];
      const upcomingEvents: PlaylistItem[] = [];

      const sortedLiveEvent: PlaylistItem[] = [...(playlists.playlist || [])].sort(
        (a, b) => new Date(a.scheduledStart ?? 0).getTime() - new Date(b.scheduledStart ?? 0).getTime(),
      );
      sortedLiveEvent.forEach((item: PlaylistItem) => {
        if (item.scheduledStart) {
          const startTime = Math.floor(new Date(item.scheduledStart).getTime() / 1000);
          const endTime = item.scheduledEnd ? Math.floor(new Date(item.scheduledEnd).getTime() / 1000) : startTime + item.duration;
          const now = Math.floor(Date.now() / 1000);

          if (now >= startTime && now < endTime) {
            liveEvent.push(item);
          } else if (startTime > now) {
            upcomingEvents.push(item);
          }
        }
      });

      return { liveEvent, upcomingEvents };
    }

    return { liveEvent: [], upcomingEvents: [] };
  }, []);

  useEffect(() => {
    const { liveEvent, upcomingEvents } = getLiveEventPlaylists(playlist);

    const timestamps = [
      liveEvent[0]?.scheduledEnd ? new Date(liveEvent[0].scheduledEnd).getTime() : null,
      liveEvent[1]?.scheduledStart ? new Date(liveEvent[1].scheduledStart).getTime() : null,
      upcomingEvents[0]?.scheduledStart ? new Date(upcomingEvents[0].scheduledStart).getTime() : null,
    ].filter((t): t is number => t !== null);

    const now = Date.now();
    const futureTimestamps = timestamps.filter((t) => t > now);
    const leastFutureTimestamp = futureTimestamps.length > 0 ? Math.min(...futureTimestamps) : null;

    const timeoutMs = leastFutureTimestamp ? leastFutureTimestamp - now : null;

    let timeoutId: ReturnType<typeof setTimeout>;

    if (timeoutMs && timeoutMs > 0) {
      timeoutId = setTimeout(() => {
        liveEventPlayListCall();
      }, timeoutMs);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [playlist, liveEventPlayListCall]);

  if (error || !playlist?.playlist) return <h2 className={styles.error}>Could not load items</h2>;

  return (
    <div className={classNames(styles.shelf)} ref={ref}>
      {featured ? null : loading ? (
        <div className={styles.loadingTitle} />
      ) : (
        <div className={styles.sliderHeader} style={{ marginBottom: isShowTopTenIcon() ? '1%' : '0%' }}>
          <h2 className={classNames(styles.title)} style={{ ...universalStyles.videostripTitle, ...universalStyles.secondaryFontFamily }}>
            {title || playlist.title}
          </h2>{' '}
          {!haveSeemoreOption && (
            <div className={styles.seeMoreOption}>
              <span onClick={() => seeMoreRedirection(playlist)} style={{ color: themeConfig?.primary }} className={styles.seeMoreText}>
                {t('see_more')}
              </span>

              <svg
                width="7"
                height="12"
                viewBox="0 0 7 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.seeMoreIcon}
                onClick={() => seeMoreRedirection(playlist)}
              >
                <path
                  d="M0.700781 11.7001C0.517448 11.5167 0.425781 11.2834 0.425781 11.0001C0.425781 10.7167 0.517448 10.4834 0.700781 10.3001L4.60078 6.40007L0.700781 2.50007C0.517448 2.31674 0.425781 2.08341 0.425781 1.80007C0.425781 1.51674 0.517448 1.28341 0.700781 1.10007C0.884114 0.91674 1.11745 0.825073 1.40078 0.825073C1.68411 0.825073 1.91745 0.91674 2.10078 1.10007L6.70078 5.70007C6.80078 5.80007 6.87178 5.90841 6.91378 6.02507C6.95511 6.14174 6.97578 6.26674 6.97578 6.40007C6.97578 6.53341 6.95511 6.65841 6.91378 6.77507C6.87178 6.89174 6.80078 7.00007 6.70078 7.10007L2.10078 11.7001C1.91745 11.8834 1.68411 11.9751 1.40078 11.9751C1.11745 11.9751 0.884114 11.8834 0.700781 11.7001Z"
                  fill={universalStyles.seeMoreIcon.color}
                />
              </svg>
            </div>
          )}
        </div>
      )}
      <TileSlider<PlaylistItem>
        className={styles.slider}
        items={playlist.playlist}
        tilesToShow={tilesToShow}
        cycleMode={featured ? 'restart' : 'stop'}
        showControls={!loading}
        spacing={16}
        renderLeftControl={renderLeftControl}
        renderRightControl={renderRightControl}
        renderPagination={renderPagination}
        renderTile={renderTile}
        initialIndex={focusedIndex}
        onFocusChange={onFocusChange}
        onLastItemClick={onLastItemClick}
        isShowTopTenIconFlag={isShowTopTenIcon()}
        isOverlayTestVisible={isOverlayTestVisible()}
        shelfTitle={title || playlist.title}
        featured={featured}
        activeScreen={activeScreen}
      />
    </div>
  );
};

export default createInjectableComponent(ShelfIdentifier, Shelf);
